import React, { useEffect, useMemo } from "react";
import { Chip, Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { HubHookFormSelectV2 } from "../HubHookFormSelectV2";
import { useGetPartQuotes } from "../../hooks/react-query/useSalesData";
import { QUOTE_STAGE_LABELS } from "../../constants/salesStages";
import { getDollarValue } from "../../helpers/currency";
import { HubHookFormSelect } from "../HubHookFormSelect";

const stageOf = (stage) =>
  QUOTE_STAGE_LABELS.find((s) => s.id === stage) || {
    label: `Stage ${stage}`,
    flow: "grey",
  };

// Renders inside the dropdown's option list. Receives the option object the
// select was given (id + label + extras).
const OptionRender = ({ quoteNumber, stage, total }) => {
  const stageMeta = stageOf(stage);
  return (
    <Stack
      direction="row"
      gap="10px"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: "100%", paddingRight: "25px" }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 500, fontSize: "12px !important" }}
      >
        {quoteNumber}
      </Typography>
      <Chip
        label={stageMeta.label}
        flow={stageMeta.flow}
        variant="colorTag"
        sx={{ fontSize: 10, fontWeight: 500, height: 20 }}
      />
      <Typography variant="caption" sx={{ minWidth: 70, textAlign: "right" }}>
        {total != null ? getDollarValue(Number(total)) : "—"}
      </Typography>
    </Stack>
  );
};

export const QuoteSelectForPart = ({
  partId,
  name = "quoteId",
  priceField = "details.lastSentPrice",
  label = "Source Quote",
  defaultQuotes,
  ...rest
}) => {
  const { setValue } = useFormContext();
  const { data } = useGetPartQuotes(partId, !defaultQuotes);
  const quotes = defaultQuotes || data || [];

  const options = useMemo(
    () =>
      quotes.map((q) => ({
        id: q.id,
        label: q.quoteNumber,
        quoteNumber: q.quoteNumber,
        stage: q.stage,
        total: q.total,
      })),
    [quotes],
  );

  const handleChange = (_, selected) => {
    if (!selected) return;
    if (selected.total != null) {
      setValue(priceField, Number(selected.total), { shouldDirty: true });
    }
  };

  return (
    <HubHookFormSelect
      name={name}
      label={label}
      options={options}
      RenderComponent={OptionRender}
      renderValue={(value) => {
        const opt = options.find((o) => o.id === value);
        return opt ? <OptionRender {...opt} /> : null;
      }}
      onChange={handleChange}
      shouldUnregister={false}
      disabled={!partId}
      allowNull
      {...rest}
    />
  );
};

export default QuoteSelectForPart;
